import React, {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTags,
  FaMoneyBillWave,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const ModalAddFee = React.lazy(() => import("../dialog/ModalAddFee"));

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function isSpecialFee(item) {
  return (
    item?.is_special_fee === 1 ||
    item?.is_special_fee === "1" ||
    item?.is_special_fee === true
  );
}

//Created By Cursor
function feeInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

const FeeCard = memo(function FeeCard({ item, index }) {
  const special = isSpecialFee(item);
  const remark = String(item.remark || "").trim();
  const service = String(item.service_name || "").trim();
  const feeType = String(item.fee_type || "").trim();

  return (
    <article
      className={`fnl-card${special ? " is-special" : ""}`}
      style={{ "--fnl-i": index }}
    >
      <div className="fnl-card__top">
        <span className="fnl-card__avatar" aria-hidden>
          {feeInitials(item.fee_name)}
        </span>
        <div className="min-w-0">
          <h3 className="fnl-card__title">{item.fee_name || "Untitled fee"}</h3>
          <div className="fnl-card__chips">
            {feeType ? <span className="fnl-chip">{feeType}</span> : null}
            <span
              className={`fnl-chip${
                special ? " fnl-chip--special" : " fnl-chip--all"
              }`}
            >
              {special ? "By admission type" : "All students"}
            </span>
          </div>
        </div>
      </div>
      {(remark || service) && (
        <div className="fnl-card__body">
          {remark ? <p className="fnl-card__remark">{remark}</p> : null}
          {service ? (
            <p className="fnl-card__service">
              <FaTags aria-hidden /> {service}
            </p>
          ) : null}
        </div>
      )}
    </article>
  );
});

//Created By Cursor
export default function FeeNameList() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fees, setFees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddFee, setShowAddFee] = useState(false);

  //Created By Cursor
  const loadFees = useCallback(async ({ soft = false } = {}) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (soft) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getFeeNameList.php`, {
        params: { school_code: schoolCode },
      });
      if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
        setFees(res.data.data);
      } else {
        setFees([]);
        myToast.notify_warning(
          res.data?.api_remark || "Could not load fee names."
        );
      }
    } catch (err) {
      console.error("[FeeNameList] load error:", err);
      setFees([]);
      myToast.notify_warning("Failed to load fee names.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadFees();
  }, [loadFees]);

  //Created By Cursor
  const filteredFees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return fees;
    return fees.filter((item) => {
      const hay = [
        item.fee_name,
        item.remark,
        item.fee_type,
        item.service_name,
        isSpecialFee(item) ? "special admission" : "all student",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [fees, searchTerm]);

  //Created By Cursor
  const stats = useMemo(() => {
    let special = 0;
    fees.forEach((f) => {
      if (isSpecialFee(f)) special += 1;
    });
    return {
      total: fees.length,
      special,
      standard: Math.max(0, fees.length - special),
    };
  }, [fees]);

  //Created By Cursor
  const handleCloseAdd = useCallback(() => {
    setShowAddFee(false);
  }, []);

  //Created By Cursor
  const handleAddSuccess = useCallback(() => {
    setShowAddFee(false);
    loadFees({ soft: true });
  }, [loadFees]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page fnl-page"
      style={{ background: "#f5f7fb", fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="fnl-crumb"
                onClick={() => history.goBack()}
              >
                Settings
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Fee Name List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaMoneyBillWave />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Fee Name List</h1>
              <p className="sm-hero__sub">
                Applicable fees for class fee setup
                {!loading ? ` · ${stats.total} fee${stats.total === 1 ? "" : "s"}` : ""}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="fnl-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search fee names…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search fee names"
                disabled={loading}
              />
            </div>
            <button
              type="button"
              className={`fnl-icon-btn${refreshing ? " is-spinning" : ""}`}
              onClick={() => loadFees({ soft: true })}
              disabled={loading || refreshing}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="fnl-btn fnl-btn--primary"
              onClick={() => setShowAddFee(true)}
              disabled={loading}
            >
              <FaPlus />
              <span>Add New Fee</span>
            </button>
          </div>
        </header>

        <div className="fnl-content">
          {loading ? (
            <div className="fnl-loading">
              <MyLoader />
              <p>Loading fee names…</p>
            </div>
          ) : (
            <>
              <div className="fnl-stats" aria-label="Fee summary">
                <div className="fnl-stat">
                  <strong>{stats.total}</strong>
                  <span>Total</span>
                </div>
                <div className="fnl-stat fnl-stat--all">
                  <strong>{stats.standard}</strong>
                  <span>All students</span>
                </div>
                <div className="fnl-stat fnl-stat--special">
                  <strong>{stats.special}</strong>
                  <span>By admission type</span>
                </div>
              </div>

              {filteredFees.length === 0 ? (
                <div className="fnl-empty">
                  <div className="fnl-empty__icon" aria-hidden>
                    <FaMoneyBillWave />
                  </div>
                  <p className="fnl-empty__title">
                    {fees.length === 0 ? "No fees yet" : "No matching fees"}
                  </p>
                  <p className="fnl-empty__hint">
                    {fees.length === 0
                      ? "Add an applicable fee name to use in class fee setup."
                      : "Try a different search term."}
                  </p>
                  {fees.length === 0 ? (
                    <button
                      type="button"
                      className="fnl-btn fnl-btn--primary"
                      onClick={() => setShowAddFee(true)}
                    >
                      <FaPlus />
                      <span>Add New Fee</span>
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="fnl-grid">
                  {filteredFees.map((item, index) => (
                    <FeeCard
                      key={item.fee_id || `${item.fee_name}-${index}`}
                      item={item}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showAddFee ? (
        <Suspense fallback={<MyLoader />}>
          <ModalAddFee
            action={handleCloseAdd}
            action_submit={handleAddSuccess}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
